import React, { useState } from 'react'
import { Scale, ChevronDown, ChevronRight } from 'lucide-react'
import { formatSignedPercent } from '../utils/format'
import { toneColor } from '../utils/tone'

const returnTone = (value) => ((value ?? 0) >= 0 ? 'bull' : 'bear')

const formatBasisPoints = (value) => {
  const points = Math.round((value ?? 0) * 100)
  return `${points > 0 ? '+' : ''}${points}bp`
}

const bestReference = (baskets) =>
  baskets.reduce((best, b) => (Math.abs(b.correlation ?? 0) > Math.abs(best.correlation ?? 0) ? b : best), baskets[0])

export default function MarketContextPanel({ context }) {
  const [detailsOpen, setDetailsOpen] = useState(false)

  const groups = context?.groups?.filter((g) => g.members?.length) ?? []
  const attribution = context?.attribution
  const baskets = attribution?.baskets ?? []
  if (!groups.length || !baskets.length) return null

  const reference = bestReference(baskets)
  const residuals = baskets.map((b) => b.companySpecific ?? 0)
  const low = Math.min(...residuals)
  const high = Math.max(...residuals)

  return (
    <div className="onds-card p-4 md:p-5">
      <div className="flex items-center justify-between gap-3 mb-1 flex-wrap">
        <div className="flex items-center gap-2">
          <Scale className="h-5 w-5" style={{ color: 'var(--onds-accent)' }} />
          <h2 className="text-base md:text-lg font-bold">Market vs Company</h2>
        </div>
        <div className="text-sm">
          <span className="text-[var(--onds-text-dim)]">{attribution.symbol} · {attribution.periodLabel}: </span>
          <span className="font-bold tabular-nums" style={{ color: toneColor(returnTone(attribution.subjectReturn)) }}>
            {formatSignedPercent(attribution.subjectReturn)}
          </span>
        </div>
      </div>

      <p className="text-xs text-[var(--onds-text-dim)] mb-4">
        Company-specific residual ranges{' '}
        <span className="font-semibold" style={{ color: toneColor(returnTone(low)) }}>{formatSignedPercent(low)}</span>
        {' to '}
        <span className="font-semibold" style={{ color: toneColor(returnTone(high)) }}>{formatSignedPercent(high)}</span>
        {' '}depending on reference basket. Closest match is{' '}
        <span className="font-semibold" style={{ color: 'var(--onds-accent)' }}>{reference.label}</span>
        {' '}(correlation {(reference.correlation ?? 0).toFixed(2)}).
      </p>

      <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-3">
        {baskets.map((basket) => (
          <BasketCard key={basket.key} basket={basket} highlight={basket.key === reference.key} />
        ))}
      </div>

      <button
        onClick={() => setDetailsOpen((open) => !open)}
        className="flex items-center gap-1.5 mt-4 text-xs font-semibold"
        style={{ color: 'var(--onds-text-dim)' }}
      >
        {detailsOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
        {detailsOpen ? 'Hide' : 'Show'} raw sector, rates &amp; global data
      </button>

      {detailsOpen && (
        <div className="flex flex-col gap-4 mt-3">
          {groups.map((group) => (
            <GroupTable key={group.key} group={group} />
          ))}
        </div>
      )}
    </div>
  )
}

function BasketCard({ basket, highlight }) {
  const share = Math.min(100, Math.max(0, Math.round((basket.marketDrivenShare ?? 0) * 100)))

  return (
    <div
      className="rounded-lg p-3 border"
      style={{
        backgroundColor: 'var(--onds-surface)',
        borderColor: highlight ? 'var(--onds-accent)' : 'var(--onds-border)',
      }}
    >
      <p className="text-[11px] font-bold leading-tight mb-2">{basket.label}</p>

      <div className="flex items-baseline justify-between mb-0.5">
        <span className="text-[10px] text-[var(--onds-text-dim)]">Company-specific</span>
        <span className="text-sm font-bold tabular-nums" style={{ color: toneColor(returnTone(basket.companySpecific)) }}>
          {formatSignedPercent(basket.companySpecific)}
        </span>
      </div>
      <div className="flex items-baseline justify-between mb-2">
        <span className="text-[10px] text-[var(--onds-text-dim)]">Market-driven</span>
        <span className="text-xs font-semibold tabular-nums" style={{ color: toneColor(returnTone(basket.marketDriven)) }}>
          {formatSignedPercent(basket.marketDriven)}
        </span>
      </div>

      <div className="h-1 rounded-full overflow-hidden" style={{ backgroundColor: 'var(--onds-border)' }}>
        <div className="h-full rounded-full" style={{ width: `${share}%`, backgroundColor: 'var(--onds-accent)' }} />
      </div>
      <p className="text-[10px] text-[var(--onds-text-dim)] mt-1 tabular-nums">
        {share}% explained · beta {(basket.beta ?? 0).toFixed(2)} · corr {(basket.correlation ?? 0).toFixed(2)}
      </p>
    </div>
  )
}

function GroupTable({ group }) {
  const periods = []
  for (const member of group.members) {
    for (const key of Object.keys(member.returns ?? {})) {
      if (!periods.includes(key)) periods.push(key)
    }
  }

  return (
    <div>
      <div className="flex items-center gap-2 mb-1.5 flex-wrap">
        <h3 className="text-xs font-bold">{group.label}</h3>
        {group.isYield && <span className="onds-chip text-[10px]">basis points</span>}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-[10px] text-[var(--onds-text-dim)]">
              <th className="text-left font-semibold py-1 pr-3">Symbol</th>
              {periods.map((p) => (
                <th key={p} className="text-right font-semibold py-1 pr-3 whitespace-nowrap">{p}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {group.members.map((member) => (
              <tr key={member.symbol} className="border-t" style={{ borderColor: 'var(--onds-border)' }}>
                <td className="py-1 pr-3 font-semibold whitespace-nowrap">{member.symbol}</td>
                {periods.map((p) => {
                  const value = member.returns?.[p]
                  if (value === undefined) return <td key={p} className="py-1 pr-3 text-right text-[var(--onds-text-dim)]">—</td>
                  return (
                    <td
                      key={p}
                      className="py-1 pr-3 text-right tabular-nums font-semibold"
                      style={{ color: toneColor(returnTone(value)) }}
                    >
                      {group.isYield ? formatBasisPoints(value) : formatSignedPercent(value)}
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
